/**
 * World - runtime scene manager. Owns the Three.js scene graph, coordinates actor lifecycles,
 * and manages global services (input, physics, navigation, networking).
 */
import * as THREE from 'three';

import { Actor } from '../actors/Actor.js';
import { GlobalParticleManager } from '../actors/GlobalParticleManager.js';
import { PlayerController } from '../actors/PlayerController.js';
import { LightComponent, ModelMeshComponent } from '../components/index.js';
import { NavigationRole } from '../components/PrimitiveComponent.js';
import { BaseInstancedMeshComponent } from '../components/visual/BaseInstancedMeshComponent.js';
import { InstancedModelMeshComponent } from '../components/visual/InstancedModelMeshComponent.js';
import { MeshComponent } from '../components/visual/MeshComponent.js';
import { ManifoldWrapper } from '../csg/ManifoldWrapper.js';
import { MaterialUtils } from '../materials.js';
import { ServerAuthorityNetWorld } from '../multiplayer/ServerAuthorityNetWorld.js';
import { createNavigationServer } from '../navigation/NavigationServer.js';
import { createPhysicsEngine } from '../physics/createPhysicsEngine.js';
import { PhysicsMotionType } from '../physics/PhysicsTypes.js';
import { CsmManager } from '../render/CsmManager.js';
import { type PostProcessConfiguration, PostProcessManager } from '../render/PostProcessManager.js';
import { PluginManager } from '../scripting/PluginManager.js';
import { AbstractConstructor, ClassRegistry, Constructor, EngineClass } from '../systems/ClassRegistry.js';
import { ConsoleCommand, ConsoleManager } from '../systems/ConsoleManager.js';
import { ConsoleVariableHandler } from '../systems/ConsoleVariableHandler.js';
import { InputManager } from '../systems/InputManager.js';
import { ObjectNamingSystem } from '../systems/ObjectNamingSystem.js';
import { ObjectTracker } from '../systems/ObjectTracker.js';
import { StatsDisplay } from '../systems/Stats.js';
import { TimerSystem } from '../systems/TimerSystem.js';
import { TweenManager } from '../systems/TweenManager.js';
import { WorldSerializer } from '../systems/WorldSerialization.js';
import { UIManager } from '../ui/UIManager.js';
import { Delegate } from '../utils/Delegate.js';
import { disposeObject3DSubtree } from '../utils/disposeThreeSubtree.js';
import { GlobalAudioManager } from '../utils/GlobalAudioManager.js';
import { resourceManager } from '../utils/ResourceManager.js';
import { SceneGraphUtils } from '../utils/SceneGraphUtils.js';
import { property } from '../utils/serialization/decorator.js';
import { PrefabManager } from '../utils/serialization/prefabManager.js';
import { ISerializableObjectTag } from '../utils/serialization/serializable.js';
import { spawn } from '../utils/Spawn.js';
import { ensure } from '../utils/Utils.js';
import { shortUUID } from '../utils/uuid.js';


import { GameMode } from './GameMode.js';
import { Playable, PlayState } from './Playable.js';

import type { BaseGameLoop, DebugUIMode } from './GameLoop.js';
import type { Pawn } from '../actors/Pawn.js';
import type { SceneEnvironmentComponent } from '../components/index.js';
import type { SceneComponent } from '../components/SceneComponent.js';
import type { NetWorld } from '../multiplayer/index.js';
import type { INavigationServer, NavigationOptions } from '../navigation/NavigationServer.js';
import type { IPhysicsEngine, PhysicsOptions } from '../physics/PhysicsEngine.js';
import type { IGenesysRenderer } from '../render/Renderer.js';
import type { AssetPath } from '../storage/storageProvider.js';
import type { SceneResources } from '../systems/WorldSerialization.js';
import type { ISceneGraphDebugNodeGenerator, SceneGraphDebugNode } from '../utils/SceneGraphUtils.js';
import type { IDumper, ILoader } from '../utils/serialization/factories.js';
import type { SerializedData } from '../utils/serialization/types.js';
import type { Spawnable } from '../utils/Spawn.js';
import type { Inspector } from 'three/addons/inspector/Inspector.js';

function resolveWorldDebugUIMode(options: WorldOptions): DebugUIMode {
  if (options.headless) {
    return 'none';
  }
  return options.debugUIMode ?? 'inspector';
}

interface WorldOptionsBase {
  backgroundColor?: THREE.ColorRepresentation;
  physicsOptions: PhysicsOptions
  navigationOptions: NavigationOptions
  useManifold?: boolean;
  gameMode?: GameMode;
  gameModeClass?: Spawnable<GameMode>; // Class name or constructor to instantiate for gameMode
  enableGamepad?: boolean;
  gameId?: string;
  gameLoop?: WeakRef<BaseGameLoop>;
  netWorld?: NetWorld;
  /**
   * Debug HUD mode (non-headless only). Defaults to `inspector` when omitted.
   * @see {@link BaseGameLoopOptions.debugUIMode}
   */
  debugUIMode?: DebugUIMode;
}

export interface HeadlessWorldOptions extends WorldOptionsBase {
  headless: true;
  rendererDomElement?: never;
  gameContainer?: never;
}

export interface RenderedWorldOptions extends WorldOptionsBase {
  headless: false;
  rendererDomElement: HTMLElement;
  gameContainer: HTMLElement;
}

export type WorldOptions = HeadlessWorldOptions | RenderedWorldOptions;

export interface WorldDescriptionOptions {
  // actor IDs, if specified, actors not in this list will be omitted and only contain basic uuid and name info
  specifiedActors?: string[];

  // whether to include the actor's components in the description
  includeComponentsDetails?: boolean;
}

export interface RenderWindowSize {
  width: number;
  height: number;
}

export type GenericWorldDelegate = Delegate<[World], void>;

@EngineClass('World')
export class World extends Playable implements ISceneGraphDebugNodeGenerator {
  readonly [ISerializableObjectTag] = true;

  public readonly postProcessManager: PostProcessManager = new PostProcessManager();

  /** Fired once, immediately after all actors and subsystems have had beginPlay called. */
  public readonly afterBeginPlay: GenericWorldDelegate = new Delegate<[World], void>();

  public readonly uuid: string = shortUUID();
  public readonly name: string;
  public readonly scene: THREE.Scene;
  public readonly inputManager: InputManager;
  public readonly consoleManager: ConsoleManager;
  public readonly timerSystem: TimerSystem;
  public readonly tweenManager: TweenManager;
  public readonly uiManager: UIManager;
  public readonly gameMode: GameMode | null;

  @property({ description: 'The game mode class to use for this world' })
  public gameModeClass: Spawnable<GameMode> | null = null;
  public readonly pluginManager: PluginManager;
  private lastCameraWarningTime: number = 0;
  public readonly netWorld: NetWorld;
  public readonly audioListener: THREE.AudioListener | null;
  public readonly globalAudioManager: GlobalAudioManager;
  public readonly globalParticleManager: GlobalParticleManager;
  public editorData: Record<string, any> = {};
  public isEditorWorld: boolean = false;
  /** Renderer reference for editor worlds (gameLoop is not available in editor) */
  public editorRenderer: IGenesysRenderer | null = null;
  public sceneResources: SceneResources | null = null;
  public postProcessConfiguration: PostProcessConfiguration | null = null;
  public connectionStatusOverlay: any = null; // ConnectionStatusOverlay, but avoid circular import

  private actors: Actor[] = [];
  private playerControllers: PlayerController[] = [];
  private physicsEngine: IPhysicsEngine | null = null;
  private navigationServer: INavigationServer | null = null;
  private manifold: ManifoldWrapper | null = null;
  private navigationMeshVisualization: THREE.Group | null = null;

  // Track creation separately since null physics engine is valid when not needed
  private navigationServerCreated: boolean = false;
  private physicsEngineCreated: boolean = false;
  private manifoldCreated: boolean = false;
  private isTicking: boolean = false;
  private actorsToRemove: Actor[] = [];
  private hiddenInGameActors: Set<Actor> = new Set();
  private hiddenInGameSceneComponents: Set<SceneComponent> = new Set();
  private isPaused: boolean = false;
  private slomo: number = 1;
  private gameTime: number = 0;
  private viewTargetCameras: WeakRef<THREE.Camera>[] = [];
  private skyboxComponent: SceneEnvironmentComponent | null = null;
  private renderWindowSize: RenderWindowSize | null = null;
  private nextTickFunctions: (() => void)[] = [];
  private statsDisplay?: StatsDisplay;
  private readonly csmManager: CsmManager;

  private fallbackCamera: THREE.PerspectiveCamera;
  private cameraWarningElement: HTMLElement | null = null;

  constructor(readonly options: WorldOptions) {
    super();
    const isHeadless = options.headless;

    this.netWorld = options.netWorld ?? new ServerAuthorityNetWorld(options.gameId ?? 'default');
    this.netWorld.setWorld(this);
    this.name = ObjectNamingSystem.generateName(this);
    this.scene = new THREE.Scene();
    this.scene.setWorld(this);
    this.scene.background = new THREE.Color(options.backgroundColor);
    this.actors = [];
    this.csmManager = new CsmManager();

    if (isHeadless) {
      this.inputManager = new InputManager({
        world: new WeakRef(this),
        enableGamepad: false
      });
    } else {
      if (!options.rendererDomElement || !options.gameContainer) {
        throw new Error('rendererDomElement and gameContainer are required for non-headless World');
      }
      this.inputManager = new InputManager({
        rendererDomElement: options.rendererDomElement,
        gameContainer: options.gameContainer,
        world: new WeakRef(this),
        enableGamepad: options.enableGamepad
      });
    }

    this.timerSystem = new TimerSystem({
      world: this
    });
    this.tweenManager = new TweenManager(this);
    this.consoleManager = new ConsoleManager();

    if (!isHeadless) {
      this.inputManager.addInputHandler(this.consoleManager);
    }

    this.consoleManager.addCommandHandler(new ConsoleVariableHandler());

    if (!isHeadless && options.gameContainer && resolveWorldDebugUIMode(options) === 'stats') {
      this.statsDisplay = new StatsDisplay(options.gameContainer);
    }

    if (!isHeadless && options.gameContainer) {
      this.uiManager = new UIManager({
        container: options.gameContainer
      });
      this.inputManager.addInputHandler(this.uiManager);
    } else {
      this.uiManager = new UIManager({});
    }

    if (options.gameMode) {
      this.gameMode = options.gameMode;
      this.gameModeClass = ClassRegistry.getClassName(options.gameMode.constructor as any);
    } else if (options.gameModeClass) {
      this.gameModeClass = options.gameModeClass;
      this.gameMode = spawn(options.gameModeClass);
      console.log(`[World] Created gameMode from gameModeClass: ${this.gameModeClass}`);
    } else {
      this.gameMode = null;
      this.gameModeClass = null;
    }

    if (this.gameMode) {
      this.addActor(this.gameMode);
    }

    this.audioListener = !isHeadless ? new THREE.AudioListener() : null;

    this.globalAudioManager = GlobalAudioManager.getInstance();
    this.globalAudioManager.setWorld(this);

    this.globalParticleManager = GlobalParticleManager.create();
    this.globalParticleManager.setTransient(true);
    this.addActors(this.globalParticleManager);

    this.postProcessManager.setBeforeRenderHook((renderer, _scene, camera) => {
      this.csmManager.tickCSM(this, renderer, camera, this.scene);
    });

    this.fallbackCamera = new THREE.PerspectiveCamera(75, 1, 0.1, 1000);
    this.fallbackCamera.position.set(0, 5, 10);
    this.fallbackCamera.lookAt(0, 0, 0);

    this.pluginManager = new PluginManager(new WeakRef(this));
  }

  public serialize(dumper: IDumper): void {
    dumper.dumpValue('$uuid', this.uuid);
    dumper.dumpValue('actors', this.actors.filter(actor => !!actor));
    dumper.dumpValue('resources', this.sceneResources);
    dumper.dumpValue('editorData', this.editorData);
    dumper.dumpValue('postProcessConfiguration', this.postProcessConfiguration);
    dumper.dumpValue('settings', {
      gameModeClass: this.gameModeClass ?? undefined
    });

    dumper.dumpProperties(this);
  }

  public deserialize(loader: ILoader): void {
    (this as any).uuid = loader.loadValue('$uuid') ?? this.uuid;

    this.sceneResources = loader.loadValue('resources', this.sceneResources);
    this.editorData = loader.loadValue('editorData', this.editorData);
    const loadedPostProcessConfig = loader.loadValue('postProcessConfiguration', this.postProcessConfiguration);
    if (loadedPostProcessConfig) {
      this.setPostProcessConfiguration(loadedPostProcessConfig);
    }

    // gameModeClass extraction happens pre-construction in runtime; editor deserializes into existing world
    const rawSettings = (loader as any).currentObjectRecord?.settings;

    if (this.gameModeClass === null && rawSettings) {
      if (rawSettings.gameModeClass) {
        // New format: gameModeClass is a string
        this.gameModeClass = rawSettings.gameModeClass;
      } else if (rawSettings.gameMode) {
        // Legacy format: extract class name from serialized gameMode object
        const rawGameMode = rawSettings.gameMode;

        // V2 format: { "$bc": "ClassName" }
        if (rawGameMode.$bc) {
          this.gameModeClass = rawGameMode.$bc;
        }
        // V1 format: { "$base": { "$class": "ClassName" } }
        else if (rawGameMode.$base?.$class) {
          this.gameModeClass = rawGameMode.$base.$class;
        }
      }
    }

    const actors: Actor[] = loader.loadValue('actors');
    const newlyLoadedActors = actors.filter(actor => actor && !this.actors.includes(actor));
    this.addActors(...newlyLoadedActors);

    loader.loadProperties(this);
  }

  /**
   * Seeds prefab template data used by Loader/PrefabManager before loading serialized worlds.
   */
  public prepareLoadedPrefabs(prefabs: Record<string, SerializedData>): void {
    for (const [prefabPath, prefabData] of Object.entries(prefabs)) {
      PrefabManager.setPrefabData(prefabPath, prefabData);
    }
  }


  public get gameContainer(): HTMLElement | undefined {
    return this.options.gameContainer;
  }

  public get gameLoop(): BaseGameLoop | null {
    return this.options.gameLoop?.deref() ?? null;
  }

  /** Effective debug UI mode for this world (headless worlds report `none`). */
  public getDebugUIMode(): DebugUIMode {
    return resolveWorldDebugUIMode(this.options);
  }

  /**
   * Active Three.js addon `Inspector` when the game loop initialised it; otherwise `null`.
   */
  public getThreeInspector(): Inspector | null {
    return this.gameLoop?.getThreeInspector() ?? null;
  }

  /**
   * Adds a named parameter group to the Three Inspector when available (WebGPU common renderer).
   * @returns The group handle, or `null` if the inspector is not active.
   */
  public createThreeInspectorParametersGroup(name: string): ReturnType<Inspector['createParameters']> | null {
    return this.gameLoop?.createThreeInspectorParametersGroup(name) ?? null;
  }

  public getRenderWindowSize(): RenderWindowSize | null {
    return this.renderWindowSize;
  }

  public setNavMeshResource(path: string): void {
    this.sceneResources ??= {};
    this.sceneResources.navMesh = {
      path
    };
  }

  public clearNavMeshResource(): void {
    if (this.sceneResources) {
      this.sceneResources.navMesh = undefined;
    }
  }

  public setNavMeshSettingsResource(path: string): void {
    this.sceneResources ??= {};
    this.sceneResources.navMeshSettings = {
      path
    };
  }

  public clearNavMeshSettingsResource(): void {
    if (this.sceneResources) {
      this.sceneResources.navMeshSettings = undefined;
    }
  }

  public getSceneResources(): SceneResources | null {
    return this.sceneResources;
  }

  public getPostProcessConfiguration(): PostProcessConfiguration | null {
    return this.postProcessConfiguration;
  }

  public setPostProcessConfiguration(config: PostProcessConfiguration | null): void {
    this.postProcessConfiguration = config;
    if (config) {
      this.postProcessManager.setConfiguration(config);
    }
  }

  public updatePostProcessConfiguration(config: PostProcessConfiguration): void {
    const getEffectKey = (effect: { id?: string; type: string }) => effect.id ?? effect.type;

    if (this.postProcessConfiguration) {
      // Merge with existing configuration
      const mergedEffects = [...this.postProcessConfiguration.effects];
      for (const newEffect of config.effects) {
        const newEffectKey = getEffectKey(newEffect);
        const existingIndex = mergedEffects.findIndex(effect => getEffectKey(effect) === newEffectKey);
        if (existingIndex >= 0) {
          mergedEffects[existingIndex] = { ...mergedEffects[existingIndex], ...newEffect };
        } else {
          mergedEffects.push(newEffect);
        }
      }
      this.postProcessConfiguration = { effects: mergedEffects };
    } else {
      this.postProcessConfiguration = config;
    }

    // Always apply the merged stack so effect ordering remains stable.
    this.postProcessManager.updateConfiguration(this.postProcessConfiguration);
  }

  public setPostProcessingEnabled(enabled: boolean): void {
    this.postProcessManager.setPostProcessingEnabled(enabled);
  }

  public isPostProcessingEnabled(): boolean {
    return this.postProcessManager.getPostProcessingEnabled();
  }

  public addActor(actor: Actor): this {
    return this.addActors(actor);
  }

  public addActors(...actors: Actor[]): this {
    if (!ensure(!this.isPlayEnded())) {
      return this;
    }

    actors.forEach(actor => {
      if (!ensure(!this.actors.includes(actor))) {
        return;
      }
      this.actors.push(actor);
      actor._privateSetWorld(this);
      this.scene.add(actor.rootComponent);
      if (this.isPlaying()) {
        actor.beginPlay();
      }
      if (actor instanceof PlayerController) {
        // In multiplayer, we can have multiple player controllers (one per client)
        // In single-player, there's typically only one, but we don't enforce this restriction
        this.playerControllers.push(actor);
      }
      if (this.isEditorWorld) {
        actor.onEditorAddToWorld();
        this.onEditorActorAddToWorld?.(actor);
      }
    });

    return this;
  }

  public getPhysicsEngine(): IPhysicsEngine | null {
    return this.physicsEngine;
  }

  public getNavigationServer(): INavigationServer | null {
    return this.navigationServer;
  }

  public async createNavigationMeshFromScene(): Promise<void> {
    if (!this.navigationServerCreated) {
      throw new Error('[Navigation Server] Navigation server not created');
    }

    const navigationServer = this.getNavigationServer();
    if (!navigationServer) {
      console.error('[Navigation Server] createNavigationMeshFromScene: Navigation server not available');
      return;
    }

    const walkableMeshes: THREE.Mesh[] = [];
    const obstacleMeshes: THREE.Mesh[] = [];

    // gather all the relevant model mesh components
    let modelMeshComponents: ModelMeshComponent[] = [];
    for (const actor of this.actors) {
      modelMeshComponents.push(...actor.getComponents(ModelMeshComponent));
    }
    modelMeshComponents = modelMeshComponents.filter(component => component.getPhysicsOptions()?.motionType === PhysicsMotionType.Static && component.getNavigationGeometryRole() !== NavigationRole.Exclude);

    if (modelMeshComponents.length > 0) {
      // wait for all the models to be loaded
      await Promise.all(Object.values(resourceManager.getCachedModels()));
      // Extract meshes from ModelMeshComponents
      for (const component of modelMeshComponents) {
        const role = component.getNavigationGeometryRole();
        const target = role === NavigationRole.Obstacle ? obstacleMeshes : walkableMeshes;
        target.push(...component.getAllMeshes());
      }
    }

    // gather all the relevant mesh components
    let meshComponents: MeshComponent[] = [];
    for (const actor of this.actors) {
      meshComponents.push(...actor.getComponents(MeshComponent));
    }
    meshComponents = meshComponents.filter(component => component.getPhysicsOptions()?.motionType === PhysicsMotionType.Static && component.getNavigationGeometryRole() !== NavigationRole.Exclude);
    for (const component of meshComponents) {
      const role = component.getNavigationGeometryRole();
      const target = role === NavigationRole.Obstacle ? obstacleMeshes : walkableMeshes;
      target.push(component.mesh);
    }

    // gather all static instanced mesh components (expand each instance into world-space meshes)
    let instancedComponents: BaseInstancedMeshComponent[] = [];
    for (const actor of this.actors) {
      instancedComponents.push(...actor.getComponents(BaseInstancedMeshComponent));
    }
    instancedComponents = instancedComponents.filter(
      component => component.getPhysicsOptions()?.motionType === PhysicsMotionType.Static && component.getNavigationGeometryRole() !== NavigationRole.Exclude,
    );

    const hasInstancedGltf = instancedComponents.some(
      component => component instanceof InstancedModelMeshComponent,
    );
    if (hasInstancedGltf) {
      await Promise.all(Object.values(resourceManager.getCachedModels()));
    }

    for (const component of instancedComponents) {
      if (component instanceof InstancedModelMeshComponent && component.isLoading()) {
        continue;
      }
      if (component.getInstanceCount() === 0 || component.getInstancedMeshes().length === 0) {
        continue;
      }

      const role = component.getNavigationGeometryRole();
      const target = role === NavigationRole.Obstacle ? obstacleMeshes : walkableMeshes;
      target.push(...component.collectNavigationWorldMeshes());
    }

    if (walkableMeshes.length === 0 && obstacleMeshes.length === 0) {
      console.warn('[Navigation Server] No meshes found for navigation mesh generation');
      return;
    }

    const success = await navigationServer.createNavigationMeshFromMeshes({
      walkableMeshes,
      obstacleMeshes,
    });
    if (success) {
      console.log('[Navigation Server] Navigation mesh created successfully');
    }
    else {
      console.error('[Navigation Server] Failed to create navigation mesh');
    }
  }

  public async exportNavigationMesh(scenePath: AssetPath): Promise<{ success: boolean; data?: ArrayBuffer; filename?: string }> {
    const navigationServer = this.getNavigationServer();
    if (!navigationServer) {
      console.error('[World] exportNavigationMesh: Navigation server not available');
      return { success: false };
    }

    return await navigationServer.exportNavigationMesh(scenePath);
  }

  public async importNavigationMesh(scenePath: AssetPath): Promise<boolean> {
    const navigationServer = this.getNavigationServer();
    if (!navigationServer) {
      console.error('[World] importNavigationMesh: Navigation server not available');
      return false;
    }

    return await navigationServer.importNavigationMesh(scenePath);
  }

  public async importNavigationMeshFromData(navMeshData: ArrayBuffer): Promise<boolean> {
    const navigationServer = this.getNavigationServer();
    if (!navigationServer) {
      console.error('[World] importNavigationMeshFromData: Navigation server not available');
      return false;
    }

    return await navigationServer.importNavigationMeshFromData(navMeshData);
  }

  public clearNavigationMesh(): void {
    const navigationServer = this.getNavigationServer();
    if (!navigationServer) {
      console.error('[World] clearNavigationMesh: Navigation server not available');
      return;
    }

    navigationServer.clearNavigationMesh();
  }

  public getManifold(): ManifoldWrapper | null {
    return this.manifold;
  }

  public removeActor(actor: Actor): this {
    return this.removeActors(actor);
  }

  public removeActors(...actors: Actor[]): this {
    if (this.isTicking) {
      this.actorsToRemove.push(...actors);
      return this;
    }

    actors.forEach(actor => {
      const index = this.actors.indexOf(actor);
      if (ensure(index !== -1, `${actor.getPathName()} not found in world`)) {
        if (this.isPlaying()) {
          actor.endPlay();
        }
        if (this.isEditorWorld) {
          actor.onEditorRemoveFromWorld();
          this.onEditorActorRemoveFromWorld?.(actor);
        }
        actor._privateSetWorld(null);
        this.actors.splice(index, 1);
        actor.rootComponent.removeFromParent();
        if (actor instanceof PlayerController) {
          const index = this.playerControllers.indexOf(actor);
          if (index !== -1) {
            this.playerControllers.splice(index, 1);
          }
        }
      }
    });

    return this;
  }

  /** Return the index of an actor in the internal array, or -1. */
  public getActorIndex(actor: Actor): number {
    return this.actors.indexOf(actor);
  }

  /**
   * Remove the actor matching `uuid`, then insert `newActor` at the same
   * index in the actors array. Runs full lifecycle (beginPlay / endPlay)
   * on both actors when the world is in play. Returns `true` if the swap succeeded.
   *
   * @remarks Do not call while {@link World.tick} is running (`isTicking`); use after the frame.
   * Deferred removal during ticks would leave the array out of sync. This method then returns `false`.
   */
  public replaceActorByUUID(uuid: string, newActor: Actor): boolean {
    if (this.isTicking) {
      return false;
    }

    const oldActor = this.actors.find(a => a.uuid === uuid) ?? null;
    if (oldActor === null) {
      return false;
    }
    if (oldActor === newActor) {
      return false;
    }
    if (this.actors.includes(newActor)) {
      return false;
    }

    const index = this.actors.indexOf(oldActor);
    this.removeActor(oldActor);
    this.addActor(newActor);
    // removeActor spliced out, addActor pushed to end. Move new actor to the old index.
    const appendedIndex = this.actors.indexOf(newActor);
    if (appendedIndex === -1) {
      return false;
    }
    if (appendedIndex !== index) {
      this.actors.splice(appendedIndex, 1);
      this.actors.splice(index, 0, newActor);
    }
    return true;
  }

  public runInNextTick(fn: () => void): void {
    this.nextTickFunctions.push(fn);
  }

  @THREE.profile
  private tickActorsPrePhysics(deltaTime: number) {
    for (const actor of this.actors) {
      actor.tickPrePhysics(deltaTime);
    }
  }

  @THREE.profile
  private tickActorsPostPhysics(deltaTime: number) {
    for (const actor of this.actors) {
      actor.tickPostPhysics(deltaTime);
    }
  }

  @THREE.profile
  public tickInEditor(deltaTime: number): void {
    if (deltaTime <= 0) {
      return;
    }

    this.isTicking = true;

    for (const actor of this.actors) {
      actor.tickInEditor(deltaTime);
    }

    this.isTicking = false;
    this.removeActors(...this.actorsToRemove);
    this.actorsToRemove = [];
  }

  @THREE.profile
  public tick(deltaTime: number): void {
    deltaTime *= this.slomo;
    if (this.playState !== PlayState.Playing) {
      return;
    }

    if (this.isPaused) {
      return;
    }

    if (deltaTime <= 0) {
      return;
    }

    this.gameTime += deltaTime;
    this.isTicking = true;

    // Execute functions scheduled for this tick
    if (this.nextTickFunctions.length > 0) {
      const functionsToExecute = [...this.nextTickFunctions];
      this.nextTickFunctions = [];
      for (const fn of functionsToExecute) {
        try {
          fn();
        } catch (error) {
          console.error('Error executing next tick function:', error);
        }
      }
    }

    this.timerSystem.tick(deltaTime);

    this.tweenManager.update(deltaTime);

    this.tickActorsPrePhysics(deltaTime);

    if (this.physicsEngine) {
      this.physicsEngine.tick(deltaTime);
    }

    this.tickActorsPostPhysics(deltaTime);

    this.netWorld.tick(deltaTime);

    this.statsDisplay?.tick();

    // sync the audio listener to the active camera
    const camera = this.getActiveCamera();
    if (camera && this.audioListener) {
      this.audioListener.setWorldTransform(camera.getWorldTransform());
    }

    this.isTicking = false;
    this.removeActors(...this.actorsToRemove);
    this.actorsToRemove = [];
  }

  public async createPhysicsEngine(): Promise<void> {
    if (this.physicsEngineCreated) {
      throw new Error('Physics engine already created');
    }

    this.physicsEngine = await createPhysicsEngine(this.options.physicsOptions, this);
    console.log('Physics engine created:', ClassRegistry.getClassOrConstructorName(this.physicsEngine));
    this.physicsEngineCreated = true;
  }

  public async createManifold(): Promise<void> {
    if (this.manifoldCreated) {
      throw new Error('Manifold already created');
    }

    if (!this.options.useManifold) {
      return;
    }

    this.manifold = new ManifoldWrapper();
    await this.manifold?.initManifold();
    this.manifoldCreated = true;
  }

  public async createNavigationServer(): Promise<void> {
    if (this.navigationServerCreated) {
      throw new Error('Navigation server already created');
    }

    this.navigationServer = await createNavigationServer(this.options.navigationOptions);
    console.log('Navigation server created:', ClassRegistry.getClassOrConstructorName(this.navigationServer));
    this.navigationServerCreated = true;
  }

  public generateDebugNode(): SceneGraphDebugNode | null {
    const children: (SceneGraphDebugNode | null)[] = [];
    if (this.gameMode) {
      children.push(this.gameMode.generateDebugNode());
    }
    for (const actor of this.actors) {
      children.push(actor.generateDebugNode());
    }
    return {
      title: this.name,
      children,
      data: {
        uuid: this.uuid,
        ...this.options
      }
    };
  }

  public getFirstActor<T extends Actor>(type: Constructor<T>): T | null {
    return this.actors.find(actor => actor instanceof type) as T | null;
  }

  public getComponent<T extends SceneComponent>(type: Constructor<T>): T | null {
    for (const actor of this.actors) {
      const component = actor.getComponent(type);
      if (component) {
        return component;
      }
    }
    return null;
  }

  public getActors<T extends Actor>(type: AbstractConstructor<T> = Actor as any): T[] {
    return this.actors.filter(actor => actor instanceof type) as T[];
  }

  public getActorByName<T extends Actor>(name: string): T | null {
    return this.actors.find(actor => actor.name === name) as T | null;
  }

  public getActorByUuid<T extends Actor>(uuid: string): T | null {
    return this.actors.find(actor => actor.uuid === uuid) as T | null;
  }

  public getActorsByPredicate(predicate: (actor: Actor) => boolean): Actor[] {
    return this.actors.filter(predicate);
  }

  public getFirstActorByTag(tag: string): Actor | null {
    return this.actors.find(actor => actor.hasActorTag(tag)) ?? null;
  }

  public getActorsByTag(tag: string): Actor[] {
    return this.actors.filter(actor => actor.hasActorTag(tag));
  }

  public getFirstActorByAnyTag(tags: string[]): Actor | null {
    return this.actors.find(actor => actor.hasAnyActorTag(tags)) ?? null;
  }

  public getActorsByAnyTag(tags: string[]): Actor[] {
    return this.actors.filter(actor => actor.hasAnyActorTag(tags));
  }

  public getFirstActorByAllTags(tags: string[]): Actor | null {
    return this.actors.find(actor => actor.hasAllActorTags(tags)) ?? null;
  }

  public getActorsByAllTags(tags: string[]): Actor[] {
    return this.actors.filter(actor => actor.hasAllActorTags(tags));
  }

  public getFirstPlayerPawn(): Pawn | null {
    return this.getFirstActor(PlayerController)?.getPawn() ?? null;
  }

  public getPlayerController(index: number): PlayerController | null {
    return this.playerControllers[index] ?? null;
  }

  /**
   * Clears all actors and scene contents. Removes from the live {@link World.actors} list
   * one at a time so editor removal hooks that synchronously destroy other actors cannot
   * leave stale references in a pre-built batch (which would trip `ensure` in
   * {@link World.removeActors}).
   */
  public clearWorld(): void {
    while (this.actors.length > 0) {
      const actor = this.actors[0];
      disposeObject3DSubtree(actor.rootComponent);
      this.removeActors(actor);
    }
    this.actors = [];
    for (const child of [...this.scene.children]) {
      disposeObject3DSubtree(child);
    }
    this.scene.remove(...this.scene.children);

    if (this.isEditorWorld) {
      this.onEditorActorAddToWorld = undefined;
      this.onEditorActorRemoveFromWorld = undefined;
    }
  }

  public getPathName(): string {
    return this.name;
  }

  public addBox(options: {
    position?: THREE.Vector3,
    rotation?: THREE.Euler,
    scale?: THREE.Vector3,
    width?: number,
    height?: number,
    depth?: number,
    color?: THREE.ColorRepresentation,
    motionType?: PhysicsMotionType
  }): Actor {
    return this.addBasicGeometry({
      ...options,
      geometry: new THREE.BoxGeometry(options.width, options.height, options.depth),
    });
  }

  public addSphere(options: {
    position?: THREE.Vector3,
    rotation?: THREE.Euler,
    scale?: THREE.Vector3,
    radius?: number,
    color?: THREE.ColorRepresentation,
    motionType?: PhysicsMotionType
  }): Actor {
    return this.addBasicGeometry({
      ...options,
      geometry: new THREE.SphereGeometry(options.radius),
    });
  }

  public addCapsule(options: {
    position?: THREE.Vector3,
    rotation?: THREE.Euler,
    scale?: THREE.Vector3,
    radius?: number,
    height?: number,
    color?: THREE.ColorRepresentation,
    motionType?: PhysicsMotionType
  }): Actor {
    return this.addBasicGeometry({
      ...options,
      geometry: new THREE.CapsuleGeometry(options.radius, options.height),
    });
  }

  public addCylinder(options: {
    position?: THREE.Vector3,
    rotation?: THREE.Euler,
    scale?: THREE.Vector3,
    radius?: number,
    height?: number,
    color?: THREE.ColorRepresentation,
    motionType?: PhysicsMotionType
  }): Actor {
    return this.addBasicGeometry({
      ...options,
      geometry: new THREE.CylinderGeometry(options.radius, options.radius, options.height),
    });
  }

  public addCone(options: {
    position?: THREE.Vector3,
    rotation?: THREE.Euler,
    scale?: THREE.Vector3,
    radius?: number,
    height?: number,
    color?: THREE.ColorRepresentation,
    motionType?: PhysicsMotionType
  }): Actor {
    return this.addBasicGeometry({
      ...options,
      geometry: new THREE.ConeGeometry(options.radius, options.height),
    });
  }

  public addBasicGeometry(options: {
    position?: THREE.Vector3,
    rotation?: THREE.Euler,
    scale?: THREE.Vector3,
    geometry: THREE.BufferGeometry,
    color?: THREE.ColorRepresentation,
    motionType?: PhysicsMotionType
  }): Actor {
    const actor = Actor.create({
      rootComponent: MeshComponent.create({
        position: options.position,
        rotation: options.rotation,
        scale: options.scale,
        geometry: options.geometry,
        material: new THREE.MeshStandardMaterial({ color: options.color }),
        physicsOptions: {
          enabled: !!options.motionType,
          motionType: options.motionType
        }
      })
    });
    this.addActors(actor);
    return actor;
  }

  public addAxesHelper(options: {
    position?: THREE.Vector3,
    rotation?: THREE.Euler,
    size?: number,
  }): Actor {
    const actor = Actor.create({
      position: options.position,
      rotation: options.rotation,
    });
    actor.rootComponent.add(new THREE.AxesHelper(options.size));
    this.addActors(actor);
    return actor;
  }

  public asExportedObject(): any {
    return WorldSerializer.exportObject(this);
  }

  public getGameTime(): number {
    return this.gameTime;
  }

  /**
   * Returns the currently active camera for rendering.
   *
   * Camera resolution logic (in order of priority):
   * 1. **View target stack** - Returns the topmost valid (non-garbage-collected) camera
   *    from the `viewTargets` stack. Dead weak references are cleaned up automatically.
   * 2. **Player controller camera** - If no view targets exist, falls back to the camera
   *    from the first player controller (typical for gameplay with a possessed pawn).
   * 3. **Null** - Returns `null` if no cameras are available (rendering will use fallback).
   *
   * Systems can push/pop view targets to temporarily override the camera without
   * interfering with the player controller's camera setup. Examples include:
   * - Cutscene cameras
   * - Editor viewport cameras
   * - Debug/free-fly cameras
   * - Spectator cameras
   */
  public getActiveCamera(): THREE.Camera | null {
    // Find the first valid (non-garbage-collected) view target from the top of the stack
    while (this.viewTargetCameras.length > 0) {
      const topViewTarget = this.viewTargetCameras[this.viewTargetCameras.length - 1]?.deref();
      if (topViewTarget) {
        return topViewTarget;
      }
      // Remove dead references from the top of the stack
      this.viewTargetCameras.pop();
    }

    // Fall back to the first player controller's camera (default gameplay camera)
    return this.playerControllers[0]?.getCamera() ?? null;
  }

  @THREE.profile
  public renderScene(renderer: IGenesysRenderer, windowSize: RenderWindowSize) {
    const camera = this.getActiveCamera();
    if (!camera) {
      this.showCameraWarning();
    } else {
      this.hideCameraWarning();
    }

    this.renderWindowSize = windowSize;
    const cameraToUse = camera ?? this.fallbackCamera;

    const aspect = windowSize.width / windowSize.height;
    if (cameraToUse instanceof THREE.PerspectiveCamera) {
      cameraToUse.aspect = aspect;
      cameraToUse.updateProjectionMatrix();
    } else if (cameraToUse instanceof THREE.OrthographicCamera) {
      cameraToUse.left = cameraToUse.bottom * aspect;
      cameraToUse.right = cameraToUse.top * aspect;
      cameraToUse.updateProjectionMatrix();
    }

    this.postProcessManager.renderScene(renderer, this.scene, cameraToUse, windowSize);
  }

  private showCameraWarning(): void {
    const currentTime = Date.now();
    if (currentTime - this.lastCameraWarningTime >= 10000) {
      console.warn('No camera found. Please add a three.js camera to your pawn or push one using "world.pushViewTarget(camera)".');
      this.lastCameraWarningTime = currentTime;
    }

    // Create warning element if it doesn't exist
    // disabled for now, some games have perfectly valid state with no camera (menu screens, etc.)
    /*
    const container = this.gameContainer;
    if (!this.cameraWarningElement && container) {
      this.cameraWarningElement = document.createElement('div');
      this.cameraWarningElement.innerHTML = `
        <div style="
          position: absolute;
          top: 20px;
          left: 50%;
          transform: translateX(-50%);
          background: rgba(180, 30, 30, 0.95);
          color: white;
          padding: 16px 24px;
          border-radius: 8px;
          font-family: system-ui, -apple-system, sans-serif;
          font-size: 14px;
          font-weight: 600;
          text-align: center;
          z-index: 10000;
          box-shadow: 0 4px 12px rgba(0,0,0,0.3);
          border: 2px solid rgba(255, 100, 100, 1);
          pointer-events: none;
          user-select: none;
        ">
          <div style="font-size: 18px; margin-bottom: 4px;">NO CAMERA FOUND</div>
          <div style="font-size: 14px; font-weight: normal; opacity: 0.9;">
            Add a camera to your pawn or use world.pushViewTargetCamera()
          </div>
        </div>
      `;
      container.appendChild(this.cameraWarningElement);
    }
    */

    if (this.cameraWarningElement) {
      this.cameraWarningElement.style.display = 'block';
    }
  }

  private hideCameraWarning(): void {
    if (this.cameraWarningElement) {
      this.cameraWarningElement.style.display = 'none';
    }
  }

  /**
   * Pushes a camera onto the view target stack.
   * The camera at the top of the stack becomes the active camera.
   * Does nothing if the camera is already at the top of the stack.
   */
  public pushViewTargetCamera(camera: THREE.Camera): void {
    // Clean up dead references from the top of the stack
    while (this.viewTargetCameras.length > 0) {
      const top = this.viewTargetCameras.at(-1)!.deref();
      if (top) break;
      this.viewTargetCameras.pop();
    }
    const top = this.viewTargetCameras.at(-1)?.deref();
    if (top === camera) return;
    this.viewTargetCameras.push(new WeakRef(camera));
  }

  /**
   * Removes a camera from the view target stack.
   * Removes the specified camera from anywhere in the stack (not just the top).
   * Returns the removed camera if successful and it still exists,
   * or undefined if the camera wasn't found or was garbage collected.
   */
  public removeViewTargetCamera(camera: THREE.Camera): THREE.Camera | undefined {
    // Search for the specific camera in the stack
    for (let i = this.viewTargetCameras.length - 1; i >= 0; i--) {
      const resolved = this.viewTargetCameras[i].deref();
      if (resolved === camera) {
        this.viewTargetCameras.splice(i, 1);
        return camera;
      }
      // If dead reference, remove it
      if (!resolved) {
        this.viewTargetCameras.splice(i, 1);
      }
    }

    return undefined;
  }

  public getSceneEnvironment(): SceneEnvironmentComponent | null {
    return this.skyboxComponent;
  }

  public setSceneEnvironment(component: SceneEnvironmentComponent | null): void {
    if (this.skyboxComponent && component && this.skyboxComponent !== component) {
      ensure(false, 'Scene environment already set, cannot set another');
      return;
    }
    this.skyboxComponent = component;
  }

  public destroy(): void {
    if (this.isPlaying()) {
      this.endPlay();
    }

    this.globalAudioManager.setWorld(null);
    this.scene.setWorld(null);

    this.lastCameraWarningTime = 0;

    this.statsDisplay?.dispose();
    this.statsDisplay = undefined;

    this.uiManager.destroy();

    this.inputManager.destroy();

    if (this.physicsEngine) {
      this.physicsEngine.destroy();
      this.physicsEngine = null;
    }

    this.consoleManager.removeCommandHandler(this);

    this.consoleManager.destroy();

    this.postProcessManager.destroy();

    this.navigationServer?.destroy();

    if (this.navigationMeshVisualization) {
      this.scene.remove(this.navigationMeshVisualization);
      this.navigationMeshVisualization = null;
    }

    if (this.connectionStatusOverlay) {
      this.connectionStatusOverlay.hide();
      this.connectionStatusOverlay = null;
    }

    this.csmManager.dispose();
    this.clearWorld();

    if (this.manifold) {
      this.manifold.dispose();
      this.manifold = null;
    }
    this.manifoldCreated = false;

    MaterialUtils.disposeSceneBackgroundAndEnvironment(this.scene);
    this.scene.background = null;
    this.scene.backgroundNode = null;
    this.scene.environment = null;
    this.scene.environmentNode = null;
    this.scene.fog = null;
    this.scene.overrideMaterial = null;

    this.netWorld.setWorld(null);

    // Clean up camera warning element
    if (this.cameraWarningElement) {
      this.cameraWarningElement.remove();
      this.cameraWarningElement = null;
    }
  }

  //====================================================================================================

  protected override doBeginPlay(): void {
    if (!this.physicsEngineCreated) {
      throw new Error('Physics engine not created');
    }

    this.consoleManager.addCommandHandler(this);

    // Copy actors array since beginPlay may spawn more actors
    for (const actor of [...this.actors]) {
      actor.beginPlay();
    }

    this.inputManager.beginPlay();

    // Begin play for the timer system
    this.timerSystem.beginPlay();

    this.netWorld.beginPlay();

    this.afterBeginPlay.invoke(this);
  }

  protected override doEndPlay(): void {
    for (const actor of this.actors) {
      actor.endPlay();
    }

    this.netWorld.endPlay();

    this.inputManager.endPlay();

    this.timerSystem.endPlay();

    this.globalAudioManager.endPlay();
  }

  public describe(options?: WorldDescriptionOptions): Record<string, any> {
    const description: Record<string, any> = {};
    if (this.editorData && Object.keys(this.editorData).length > 0) {
      description.info = this.editorData;
    }

    const specifiedActors = options?.specifiedActors;
    return {
      ...description,
      actorsNumber: this.actors.length,
      actors: this.actors.map(actor => actor.describe({
        includeComponentsDetails: options?.includeComponentsDetails,
        omitDescription: specifiedActors ? !specifiedActors.includes(actor.uuid) : false
      })),
    };
  }

  @ConsoleCommand('clearworld', 'Clear all actors from the world')
  private clearWorldCommand(): string {
    this.clearWorld();
    return 'World cleared';
  }

  @ConsoleCommand('dumpsg', 'Dump scene graph to HTML file (usage: dumpsg [html])')
  private dumpSG(format = 'html'): string {
    format = format.toLowerCase();
    if (format === 'html') {
      const html = SceneGraphUtils.dumpHtml(this);
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      return 'Scene graph dumped to html';
    }
    return 'Invalid format';
  }

  @ConsoleCommand('exportworld', 'Export world data to JSON file')
  private exportWorld(): boolean {
    const worldData = WorldSerializer.exportObject(this);
    const worldDataString = JSON.stringify(worldData, null, 2);
    const blob = new Blob([worldDataString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    return true;
  }

  @ConsoleCommand('pause', 'Toggle world pause state')
  private pause(): string {
    this.isPaused = !this.isPaused;
    return `World paused: ${this.isPaused}`;
  }

  @ConsoleCommand('slomo', 'Set world slow motion multiplier (usage: slomo <value>)')
  private setSlomoCommand(value: string): string {
    const num = parseFloat(value);
    if (isNaN(num)) {
      return 'Invalid number';
    }
    this.slomo = num;
    return `World slomo set to: ${this.slomo}`;
  }

  @ConsoleCommand('postprocess', 'Enable, disable, or toggle post-processing (usage: postprocess [on | off])')
  @ConsoleCommand('postprocessing', 'Alias for postprocess')
  private postprocessingCommand(arg = ''): string {
    const a = arg.toLowerCase();
    let next: boolean;
    if (a === '') {
      next = !this.isPostProcessingEnabled();
    } else if (a === '1' || a === 'on') {
      next = true;
    } else if (a === '0' || a === 'off') {
      next = false;
    } else {
      return 'Invalid argument; use on, off, or omit to toggle';
    }
    this.setPostProcessingEnabled(next);
    return `Post-processing ${this.isPostProcessingEnabled() ? 'enabled' : 'disabled'}`;
  }

  @ConsoleCommand('clearcache', 'Clear all cached resources')
  private clearCachedResources(): boolean {
    resourceManager.clearCachedResources();
    return true;
  }

  @ConsoleCommand('logcache', 'Log all cached resources to console')
  private logCachedResources(): boolean {
    resourceManager.logCachedResources();
    return true;
  }

  @ConsoleCommand('show', 'Show/hide debug visualisations (usage: show <stats | lighthelper | lightcamerahelper | navmesh | collision>)')
  private show(helperType: string): string | boolean {
    const type = helperType.toLowerCase();

    if (type === 'stats') {
      const mode = this.getDebugUIMode();
      if (mode === 'stats') {
        this.statsDisplay?.toggle();
        return 'Engine stats overlay toggled';
      }
      if (mode === 'inspector') {
        const loop = this.gameLoop;
        if (!loop) {
          return 'No game loop; cannot toggle debug UI.';
        }
        return loop.toggleThreeInspectorVisibility();
      }
      return 'Debug UI is disabled (debugUIMode: none).';
    }

    if (type === 'navmesh') {
      const navigationServer = this.getNavigationServer();
      if (!navigationServer) {
        return 'Navigation server not available';
      }

      if (this.navigationMeshVisualization) {
        this.scene.remove(this.navigationMeshVisualization);
        this.navigationMeshVisualization = null;
        return 'Navigation mesh visualization hidden';
      } else {
        const visualization = navigationServer.createDebugVisualization();
        if (!visualization) {
          console.error('[Navigation Server] show navmesh: Failed to create debug visualization');
          return 'Failed to create debug visualization';
        }

        visualization.traverse((child) => {
          child.raycast = () => { };
        });

        this.scene.add(visualization);
        this.navigationMeshVisualization = visualization;
        return 'Navigation mesh visualization shown';
      }
    }

    if (type === 'collision') {
      const physicsEngine = this.getPhysicsEngine();
      if (!physicsEngine) {
        return 'Physics engine not available';
      }

      physicsEngine.setDebugRendererEnabled(!physicsEngine.isDebugRendererEnabled());
      return 'Collision debug visualization ' + (physicsEngine.isDebugRendererEnabled() ? 'enabled' : 'disabled');
    }

    const lightComponents: LightComponent<any, any>[] = [];

    for (const actor of this.actors) {
      lightComponents.push(...actor.getComponents(LightComponent));
    }

    if (type === 'lighthelper') {
      for (const lightComponent of lightComponents) {
        lightComponent.showHelper(!lightComponent.isHelperVisible());
      }
      return `Toggled light helpers for ${lightComponents.length} lights`;
    } else if (type === 'lightcamerahelper') {
      for (const lightComponent of lightComponents) {
        lightComponent.showCameraHelper(!lightComponent.isCameraHelperVisible());
      }
      return `Toggled light camera helpers for ${lightComponents.length} lights`;
    } else {
      return 'Invalid helper type. Use: lighthelper, lightcamerahelper, navmesh, or collision';
    }
  }

  @ConsoleCommand('stat.objects', 'List the number of alive engine objects per class')
  private statObjects(): string {
    if (!ObjectTracker.isEnabled()) {
      return 'Object tracking is disabled. Set ObjectTracker.enabled = true in ObjectTracker.ts (line ~35), then rebuild';
    }

    const stats = ObjectTracker.getAliveObjectStats();

    if (stats.length === 0) {
      console.log('[ObjectTracker] No tracked objects found');
      return 'Object stats logged to browser console (F12 Console tab)';
    }

    let total = 0;
    for (const stat of stats) {
      total += stat.count;
    }

    console.log('='.repeat(50));
    console.log('Alive Engine Objects by Class');
    console.log('='.repeat(50));

    const maxClassNameLength = Math.max(...stats.map(s => s.className.length));

    for (const stat of stats) {
      const paddedClassName = stat.className.padEnd(maxClassNameLength);
      console.log(`${paddedClassName} : ${stat.count.toLocaleString()}`);
    }

    console.log('-'.repeat(50));
    console.log(`Total alive objects: ${total.toLocaleString()}`);
    console.log('='.repeat(50));

    return `Object stats logged to browser console (F12 Console tab) - ${total.toLocaleString()} total objects across ${stats.length} classes`;
  }

  public setActorHiddenInGame(actor: Actor, hidden: boolean): void {
    if (!this.isEditorWorld) {
      if (hidden) {
        this.hiddenInGameActors.add(actor);
        actor.rootComponent.traverse(obj => obj.layers.disableAll());
        this.postProcessManager.addHiddenObjects(actor.rootComponent);
      } else {
        this.postProcessManager.removeHiddenObjects(actor.rootComponent);
        actor.rootComponent.traverse(obj => obj.layers.enable(0));
        this.hiddenInGameActors.delete(actor);
      }
    }
  }

  public setSceneComponentHiddenInGame(sceneComponent: SceneComponent, hidden: boolean): void {
    if (!this.isEditorWorld) {
      if (hidden) {
        this.hiddenInGameSceneComponents.add(sceneComponent);
        sceneComponent.traverse(obj => obj.layers.disableAll());
        this.postProcessManager.addHiddenObjects(sceneComponent);
      } else {
        this.postProcessManager.removeHiddenObjects(sceneComponent);
        sceneComponent.traverse(obj => obj.layers.enable(0));
        this.hiddenInGameSceneComponents.delete(sceneComponent);
      }
    }
  }

  public getRenderer(): IGenesysRenderer | null {
    if (this.isEditorWorld && this.editorRenderer) {
      return this.editorRenderer;
    }
    return this.gameLoop?.renderer?.native ?? null;
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  private onEditorActorAddToWorld?: (actor: Actor) => void;
  private onEditorActorRemoveFromWorld?: (actor: Actor) => void;

  public setIsEditorWorld(isEditorWorld: boolean, onActorAddToWorld?: (actor: Actor) => void, onActorRemoveFromWorld?: (actor: Actor) => void): void {
    this.isEditorWorld = isEditorWorld;
    if (onActorAddToWorld) {
      this.onEditorActorAddToWorld = onActorAddToWorld;
    }
    if (onActorRemoveFromWorld) {
      this.onEditorActorRemoveFromWorld = onActorRemoveFromWorld;
    }
  }
}
